import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { type IDateRange2 } from '@floriday/floriday-ui'
import { TransactieType } from '../types/api'

export type TransactionFilterContextValue = {
  searchQuery: string
  setSearchQuery: (searchQuery: string) => void
  selectedTransactionTypes: TransactieType[]
  toggleTransactionType: (transactionType: TransactieType) => void
  dateFilter: IDateRange2 | null
  setDateFilter: (dateFilter: IDateRange2 | null) => void
  selectedBusinessAssociationIds: number[]
  setSelectedBusinessAssociationIds: (ids: number[]) => void
  filterCount: number
  clearFilters: () => void
  isFilterSidebarOpen: boolean
  openFilterSidebar: () => void
  closeFilterSidebar: () => void
}

const TransactionFilterContext =
  createContext<TransactionFilterContextValue | null>(null)

export const TransactionFilterProvider = ({ children }: PropsWithChildren) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<
    TransactieType[]
  >([])
  const [dateFilter, setDateFilter] = useState<IDateRange2 | null>(null)
  const [selectedBusinessAssociationIds, setSelectedBusinessAssociationIds] =
    useState<number[]>([])
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)

  const toggleTransactionType = useCallback(
    (transactionType: TransactieType) => {
      setSelectedTransactionTypes(current =>
        current.includes(transactionType)
          ? current.filter(type => type !== transactionType)
          : [...current, transactionType]
      )
    },
    []
  )

  const clearFilters = useCallback(() => {
    setSelectedTransactionTypes([])
    setDateFilter(null)
    setSelectedBusinessAssociationIds([])
  }, [])

  const filterCount =
    selectedTransactionTypes.length +
    (dateFilter ? 1 : 0) +
    selectedBusinessAssociationIds.length

  const value = useMemo<TransactionFilterContextValue>(
    () => ({
      searchQuery,
      setSearchQuery,
      selectedTransactionTypes,
      toggleTransactionType,
      dateFilter,
      setDateFilter,
      selectedBusinessAssociationIds,
      setSelectedBusinessAssociationIds,
      filterCount,
      clearFilters,
      isFilterSidebarOpen,
      openFilterSidebar: () => setIsFilterSidebarOpen(true),
      closeFilterSidebar: () => setIsFilterSidebarOpen(false),
    }),
    [
      searchQuery,
      selectedTransactionTypes,
      toggleTransactionType,
      dateFilter,
      selectedBusinessAssociationIds,
      filterCount,
      clearFilters,
      isFilterSidebarOpen,
    ]
  )

  return (
    <TransactionFilterContext.Provider value={value}>
      {children}
    </TransactionFilterContext.Provider>
  )
}

export const useTransactionFilters = (): TransactionFilterContextValue => {
  const context = useContext(TransactionFilterContext)
  if (!context) {
    throw new Error(
      'useTransactionFilters must be used within a TransactionFilterProvider'
    )
  }
  return context
}
